/**
 * The app's own Google Play plugin `FwpPlay` (android/app/src/main/java/ua/fwp/game/play): Play Billing, rewarded ads
 * with UMP consent, Play Games Services v2, in-app review, flexible in-app updates, Play Integrity and (in a build with
 * android/app/google-services.json) Firebase: Crashlytics, Analytics and Cloud Messaging. Typed calls over
 * capacitor.ts nativePlugin; null outside the Android app (a browser page, another shell).
 *
 * Purchase tokens and Play Games server auth codes never reach a log: Capacitor's debug logging (loggingBehavior
 * 'debug' of the debug APK) writes every plugin call's options and results to the console, which the WebView copies
 * to logcat; installPlayLogRedaction wraps that logging for FwpPlay so these values are replaced by '[redacted]', and
 * the native consume takes the token's SHA-256 (purchaseRef) instead of the token itself.
 */
import { capacitor, nativePlugin, type NativePlugin } from './capacitor';

export const PLAY_PLUGIN = 'FwpPlay';

export interface PlayCapabilities {
  billing: boolean;
  ads: boolean;
  adsConfigured: boolean;
  playGames: boolean;
  review: boolean;
  updates: boolean;
  installer: string | null;
  /** Play Integrity API (the library is in every build; it still needs the cloud project number). */
  integrity?: boolean;
  /** Firebase (Crashlytics, Analytics, Cloud Messaging): the build had a google-services.json. */
  firebase?: boolean;
}

/** Push notifications on the device (FwpPlay pushStatus). */
export interface PushStatus {
  /** Firebase Cloud Messaging is in this build. */
  available: boolean;
  /** The system lets the app show notifications (Android 13+: the POST_NOTIFICATIONS permission). */
  permission: 'granted' | 'denied' | 'prompt';
}

export interface PlayPurchase {
  productId: string;
  productIds: string[];
  purchaseToken: string;
  /** SHA-256 (hex) of the token: how consume names the purchase. */
  purchaseRef: string;
  orderId: string | null;
  purchaseState: 'purchased' | 'pending' | 'unspecified';
  acknowledged: boolean;
  quantity: number;
  purchaseTime: number;
  obfuscatedAccountId: string | null;
}

export interface PlayProduct {
  productId: string;
  title: string;
  name: string;
  description: string;
  /** Play's formatted, localized price. */
  price: string;
  priceMicros: number;
  currency: string;
}

export interface PlayPurchaseResult {
  status: 'purchased' | 'pending' | 'cancelled' | 'already_owned' | 'error';
  purchases: PlayPurchase[];
  code?: string;
  message?: string;
}

export interface ConsentInfo {
  canRequestAds: boolean;
  status: 'unknown' | 'required' | 'not_required' | 'obtained';
  privacyOptionsRequired: boolean;
  formShown: boolean;
}

/** Typed calls of the FwpPlay plugin. */
export interface PlayApi {
  capabilities(): Promise<PlayCapabilities>;
  billingStatus(): Promise<{ available: boolean; code?: string }>;
  queryProducts(productIds: string[]): Promise<{ products: PlayProduct[]; unknown: string[] }>;
  purchase(productId: string, obfuscatedAccountId: string | null): Promise<PlayPurchaseResult>;
  queryPurchases(): Promise<{ purchases: PlayPurchase[] }>;
  consume(purchaseRef: string): Promise<{ consumed: boolean; code?: string }>;
  onPurchasesUpdated(listener: (purchases: PlayPurchase[]) => void): void;
  adsConsent(opts: { debugGeography?: 'eea' | 'not_eea'; testDeviceIds?: string[] }): Promise<ConsentInfo>;
  adsPrivacyOptions(): Promise<{ shown: boolean }>;
  adsInitialize(opts?: { testDeviceIds?: string[] }): Promise<{ initialized: boolean }>;
  rewardedLoad(adUnitId: string): Promise<{ loaded: boolean }>;
  rewardedShow(ssv: { userId: string; customData: string }): Promise<{ rewarded: boolean; type: string | null; amount: number | null }>;
  gamesStatus(): Promise<{ configured: boolean; authenticated: boolean }>;
  gamesSignIn(): Promise<{ authenticated: boolean }>;
  gamesServerAuthCode(serverClientId: string, forceRefreshToken?: boolean): Promise<{ code: string }>;
  gamesUnlockAchievement(id: string): Promise<unknown>;
  gamesIncrementAchievement(id: string, steps: number): Promise<unknown>;
  gamesSubmitScore(leaderboardId: string, score: number): Promise<unknown>;
  gamesShowAchievements(): Promise<unknown>;
  gamesShowLeaderboard(leaderboardId?: string): Promise<unknown>;
  requestReview(): Promise<{ launched: boolean }>;
  updateInfo(): Promise<{ available: boolean; flexibleAllowed: boolean; availableVersionCode: number | null; stalenessDays: number | null; installStatus: string }>;
  startFlexibleUpdate(): Promise<{ started: boolean }>;
  completeFlexibleUpdate(): Promise<unknown>;
  onUpdateStatus(listener: (s: { status: string; bytesDownloaded: number; totalBytes: number }) => void): void;
  /** A Play Integrity token (Standard request) whose requestHash is the server's challenge. */
  integrityToken(cloudProjectNumber: string, requestHash: string): Promise<{ token: string }>;
  /** Crashlytics and Analytics collection on / off (the player's choice; off by default). */
  telemetryConsent(consent: { analytics: boolean; crashes: boolean }): Promise<unknown>;
  logEvent(name: string, params: Record<string, string | number>): Promise<unknown>;
  recordError(message: string): Promise<unknown>;
  pushStatus(): Promise<PushStatus>;
  /** Asks for the notification permission where the system needs one (Android 13+). */
  pushRequestPermission(): Promise<PushStatus>;
  pushToken(): Promise<{ token: string }>;
  pushDeleteToken(): Promise<unknown>;
}

function api(p: NativePlugin): PlayApi {
  return {
    capabilities: () => p.call('capabilities'),
    billingStatus: () => p.call('billingStatus'),
    queryProducts: (productIds) => p.call('queryProducts', { productIds }),
    purchase: (productId, obfuscatedAccountId) => p.call('purchase', { productId, ...(obfuscatedAccountId ? { obfuscatedAccountId } : {}) }),
    queryPurchases: () => p.call('queryPurchases'),
    consume: (purchaseRef) => p.call('consume', { purchaseRef }),
    onPurchasesUpdated: (l) => p.listen('purchasesUpdated', (d) => l(Array.isArray(d?.purchases) ? d.purchases : [])),
    adsConsent: (opts) => p.call('adsConsent', { ...opts }),
    adsPrivacyOptions: () => p.call('adsPrivacyOptions'),
    adsInitialize: (opts = {}) => p.call('adsInitialize', { ...opts }),
    rewardedLoad: (adUnitId) => p.call('rewardedLoad', { adUnitId }),
    rewardedShow: (ssv) => p.call('rewardedShow', { ...ssv }),
    gamesStatus: () => p.call('gamesStatus'),
    gamesSignIn: () => p.call('gamesSignIn'),
    gamesServerAuthCode: (serverClientId, forceRefreshToken = false) => p.call('gamesServerAuthCode', { serverClientId, forceRefreshToken }),
    gamesUnlockAchievement: (id) => p.call('gamesUnlockAchievement', { id }),
    gamesIncrementAchievement: (id, steps) => p.call('gamesIncrementAchievement', { id, steps }),
    gamesSubmitScore: (leaderboardId, score) => p.call('gamesSubmitScore', { leaderboardId, score }),
    gamesShowAchievements: () => p.call('gamesShowAchievements'),
    gamesShowLeaderboard: (leaderboardId) => p.call('gamesShowLeaderboard', leaderboardId ? { leaderboardId } : {}),
    requestReview: () => p.call('requestReview'),
    updateInfo: () => p.call('updateInfo'),
    startFlexibleUpdate: () => p.call('startFlexibleUpdate'),
    completeFlexibleUpdate: () => p.call('completeFlexibleUpdate'),
    onUpdateStatus: (l) => p.listen('updateStatus', (d) => l(d)),
    integrityToken: (cloudProjectNumber, requestHash) => p.call('integrityToken', { cloudProjectNumber, requestHash }),
    telemetryConsent: (consent) => p.call('telemetryConsent', { ...consent }),
    logEvent: (name, params) => p.call('logEvent', { name, params }),
    recordError: (message) => p.call('recordError', { message }),
    pushStatus: () => p.call('pushStatus'),
    pushRequestPermission: () => p.call('pushRequestPermission'),
    pushToken: () => p.call('pushToken'),
    pushDeleteToken: () => p.call('pushDeleteToken'),
  };
}

/** The FwpPlay plugin of the Android app, or null (a browser, a shell without it). */
export function playPlugin(win: any = globalThis): PlayApi | null {
  const p = nativePlugin(PLAY_PLUGIN, win);
  if (!p) return null;
  installPlayLogRedaction(win);
  return api(p);
}

/** Keys whose values are credentials of Google Play (see the module comment). */
const SECRET_KEYS = /^(purchaseToken|code|serverAuthCode|token|requestHash)$/;

/** A copy of a value in which every SECRET_KEYS member is '[redacted]'. */
export function redactPlayValue(v: unknown, depth = 0): unknown {
  if (depth > 8 || v === null || typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map((x) => redactPlayValue(x, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = SECRET_KEYS.test(k) && typeof x === 'string' ? '[redacted]' : redactPlayValue(x, depth + 1);
  return out;
}

/** Wraps Capacitor's call logging (logToNative / logFromNative) so FwpPlay credentials are not logged. Idempotent. */
export function installPlayLogRedaction(win: any = globalThis): void {
  const cap = capacitor(win) as (Record<string, any> & { __fwpPlayRedacted?: boolean }) | null;
  if (!cap || cap.__fwpPlayRedacted) return;
  cap.__fwpPlayRedacted = true;
  for (const name of ['logToNative', 'logFromNative'] as const) {
    const original = cap[name];
    if (typeof original !== 'function') continue;
    cap[name] = (entry: any) => original.call(cap, entry && entry.pluginId === PLAY_PLUGIN ? redactPlayValue(entry) : entry);
  }
}
